/**
 * DesktopSidebar — Desktop left sidebar navigation
 *
 * Aurora left rail with primary navigation, Ask entry point, and account footer.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@ownyou/ui-design-system';
import { NAV_ITEMS, NAV_ICONS } from './nav-constants';

export interface DesktopSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenChat?: () => void;
  className?: string;
}

function isActiveRoute(pathname: string, itemPath: string): boolean {
  if (itemPath === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(itemPath);
}

export function DesktopSidebar({ collapsed = false, onToggleCollapse, onOpenChat, className }: DesktopSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <aside
      data-testid="desktop-sidebar"
      className={cn(
        'flex flex-col h-full border-r',
        'transition-all duration-200 ease-in-out',
        collapsed ? 'w-16' : 'w-60',
        className,
      )}
      style={{
        background: 'var(--aurora-card-solid)',
        borderColor: 'var(--aurora-ink-line)',
        color: 'var(--aurora-ink)',
        width: collapsed ? 'var(--aurora-sidebar-width-collapsed)' : 'var(--aurora-sidebar-width-expanded)',
      }}
    >
      {/* Logo section */}
      <div
        className={cn(
          'flex items-center h-16 border-b shrink-0',
          collapsed ? 'justify-center px-2' : 'px-5 gap-3',
        )}
        style={{ borderColor: 'var(--aurora-ink-line)' }}
      >
        <div
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-full text-lg font-semibold"
          style={{
            background: 'var(--aurora-green)',
            color: 'var(--aurora-card-solid)',
            fontFamily: 'var(--aurora-font)',
          }}
        >
          O
        </div>
        {!collapsed && (
          <span
            data-testid="sidebar-logo"
            className="text-2xl font-semibold"
            style={{ fontFamily: 'var(--aurora-font)' }}
          >
            OwnYou
          </span>
        )}
      </div>

      {/* Navigation items */}
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="flex-1 py-3 overflow-y-auto"
      >
        <ul className="flex flex-col gap-1 px-2">
          {NAV_ITEMS.map((item) => {
            const active = isActiveRoute(location.pathname, item.path);
            const Icon = NAV_ICONS[item.id];

            return (
              <li key={item.id}>
                <button
                  data-testid={`sidebar-${item.id}`}
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                  onClick={() => navigate(item.path)}
                  className={cn(
                    'flex items-center w-full rounded-lg',
                    'transition-colors duration-150',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
                    collapsed ? 'justify-center h-10 px-0' : 'gap-3 h-10 px-3',
                    active
                      ? 'border-l-[3px]'
                      : 'border-l-[3px] border-transparent',
                  )}
                  style={{
                    background: active ? 'color-mix(in srgb, var(--aurora-accent) 12%, transparent)' : undefined,
                    borderLeftColor: active ? 'var(--aurora-accent)' : 'transparent',
                  }}
                >
                  <Icon
                    className="w-5 h-5 shrink-0"
                    style={{ color: active ? 'var(--aurora-accent)' : 'var(--aurora-ink-soft)' }}
                  />
                  {!collapsed && (
                    <span
                      className={cn(
                        'text-sm truncate',
                        active ? 'font-semibold' : 'font-medium',
                      )}
                      style={{ color: active ? 'var(--aurora-ink)' : 'var(--aurora-ink-dim)' }}
                    >
                      {item.label}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={cn('space-y-3 border-t p-3 shrink-0', collapsed && 'px-2')} style={{ borderColor: 'var(--aurora-ink-line)' }}>
        <button
          type="button"
          data-testid="sidebar-ask"
          onClick={onOpenChat}
          className={cn(
            'flex h-10 w-full items-center justify-center rounded-full text-sm font-medium',
            collapsed ? 'px-0' : 'px-3',
          )}
          style={{
            background: 'var(--aurora-ink)',
            color: 'var(--aurora-card-solid)',
          }}
          title="Ask OwnYou"
        >
          {collapsed ? '?' : 'Ask... Cmd+K'}
        </button>
        {!collapsed && (
          <div className="flex items-center gap-3 rounded-xl p-2" style={{ background: 'var(--aurora-card)' }}>
            <div className="h-8 w-8 rounded-full" style={{ background: 'var(--aurora-accent-2)' }} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" style={{ color: 'var(--aurora-ink)' }}>You</p>
              <p className="truncate text-xs" style={{ color: 'var(--aurora-ink-dim)' }}>This week: GBP 0.00</p>
            </div>
            <button
              type="button"
              aria-label="Open Settings"
              onClick={() => navigate('/settings')}
              className="rounded-full p-1"
              style={{ color: 'var(--aurora-ink-dim)' }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94s-.02-.64-.07-.94l2.03-1.58a.5.5 0 0 0 .12-.61l-1.92-3.32a.5.5 0 0 0-.59-.22l-2.39.96a7.13 7.13 0 0 0-1.62-.94l-.36-2.54a.49.49 0 0 0-.48-.41h-3.84a.49.49 0 0 0-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.5.5 0 0 0-.59.22L2.74 8.87a.5.5 0 0 0 .12.61l2.03 1.58a7.4 7.4 0 0 0-.09.94c0 .31.02.64.07.94l-2.03 1.58a.5.5 0 0 0-.12.61l1.92 3.32a.5.5 0 0 0 .59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.5.5 0 0 0-.12-.61l-2.01-1.58ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <div className="border-t p-2 shrink-0" style={{ borderColor: 'var(--aurora-ink-line)' }}>
          <button
            data-testid="sidebar-toggle"
            type="button"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={onToggleCollapse}
            className={cn(
              'flex items-center justify-center w-full h-9 rounded-lg',
              'transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50',
            )}
            style={{ color: 'var(--aurora-ink-dim)' }}
          >
            <svg
              className={cn(
                'w-5 h-5 transition-transform duration-200',
                collapsed && 'rotate-180',
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      )}
    </aside>
  );
}
